import React, { createContext, useContext, useState, useEffect } from 'react';
import * as ascApi from '../api/ascApi';
import { useEnterpriseAuth } from './EnterpriseAuthContext';

// ASCContext uses the centralized API utilities from ascApi.js

// Create the ASC context
const ASCContext = createContext();

// Custom hook to use the ASC context
export const useASC = () => {
  const context = useContext(ASCContext);
  if (!context) {
    throw new Error('useASC must be used within an ASCProvider');
  }
  return context;
};

// ASC Provider component
export function ASCProvider({ children }) {
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [surgeons, setSurgeons] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [patients, setPatients] = useState([]);
  const [surgeries, setSurgeries] = useState([]);

  const { user, federationMode } = useEnterpriseAuth();

  // Set user role
  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);

  // Fetch facility information
  const fetchFacilityInfo = async () => {
    try {
      setLoading(true);
      const data = await ascApi.getFacility();
      // If we get an array of facilities, use the first one
      if (Array.isArray(data) && data.length > 0) {
        setFacility(data[0]);
      } else {
        setFacility(data);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch surgeons
  const fetchSurgeons = async facilityId => {
    try {
      if (!facilityId) {
        setSurgeons([]);
        return;
      }

      const data = await ascApi.getSurgeons(facilityId);
      setSurgeons(data || []);
    } catch (err) {
      setSurgeons([]);
    }
  };

  // Fetch protocols
  const fetchProtocols = async facilityId => {
    try {
      if (!facilityId) {
        setProtocols([]);
        return;
      }

      const data = await ascApi.getProtocols(facilityId);
      setProtocols(data || []);
    } catch (err) {
      setProtocols([]);
    }
  };

  // Fetch patients
  const fetchPatients = async () => {
    try {
      const data = await ascApi.getPatients();

      // Ensure we have an array of patients
      let patientsArray = [];
      if (Array.isArray(data)) {
        patientsArray = data;
      } else if (data && data.patients && Array.isArray(data.patients)) {
        patientsArray = data.patients;
      }

      setPatients(patientsArray);
    } catch (err) {
      setPatients([]);
    }
  };

  // Fetch surgeries
  const fetchSurgeries = async facilityId => {
    try {
      if (!facilityId) {
        setSurgeries([]);
        return;
      }

      const data = await ascApi.getSurgeries(facilityId);
      setSurgeries(data || []);
    } catch (err) {
      setSurgeries([]);
    }
  };

  // Initialize data when component mounts
  useEffect(() => {
    // Check if we're in ASC mode - prioritize federation mode from JWT
    const isASCMode = federationMode === 'asc' || user?.mode === 'asc' || localStorage.getItem('appMode') === 'asc';

    if (user && isASCMode) {
      fetchFacilityInfo();
      fetchPatients();
    } else if (user && !isASCMode) {
      // User is logged in but not in ASC mode
      setLoading(false);
      setError('Please log in with ASC credentials to access this area.');
    } else if (!user) {
      // No user logged in
      setLoading(false);
      setError('Please log in to access this area.');
    }
  }, [user, federationMode]);

  // Fetch facility-dependent data when facility is loaded
  useEffect(() => {
    if (facility && facility.id) {
      // Set loading state while fetching data
      setLoading(true);

      Promise.all([
        fetchSurgeons(facility.id),
        fetchProtocols(facility.id),
        fetchSurgeries(facility.id),
      ])
        .catch(err => {
          setError('Failed to load some facility data. Please try refreshing the page.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [facility]);

  // Refresh data
  const refreshData = async () => {
    setLoading(true);
    setError(null);

    try {
      await fetchFacilityInfo();
      await fetchPatients();

      if (facility && facility.id) {
        await Promise.all([
          fetchSurgeons(facility.id),
          fetchProtocols(facility.id),
          fetchSurgeries(facility.id),
        ]);
      }
    } catch (err) {
      setError('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    facility,
    loading,
    error,
    userRole,
    surgeons,
    protocols,
    patients,
    surgeries,
    refreshData,
    fetchSurgeons,
    fetchProtocols,
    fetchPatients,
    fetchSurgeries,
  };

  return <ASCContext.Provider value={value}>{children}</ASCContext.Provider>;
}

export default ASCContext;
